import { useQuery } from "@tanstack/react-query"

import { pedirBinario } from "@/api/cliente"

/**
 * La foto de una ficha, traída con la sesión puesta. **El único sitio que sabe cómo
 * se pide.**
 *
 * ⚠ **No es un `<img src>` a pelo, y no se puede serlo.** `storage/` no se sirve como
 * estático (regla 8): la foto sale por un endpoint autenticado, así que llega como
 * `blob` y hay que fabricarle una URL de objeto. Un `src` directo a la ruta de la API
 * funcionaría hoy —el navegador manda la cookie— y dejaría de funcionar el día que la
 * sesión viaje en una cabecera.
 *
 * ⚠ **Vivía dentro de `RetratoPersona` y se sacó al aparecer el carnet.** Son dos
 * pinturas distintas de la misma foto: un círculo de 80 px en la ficha y un
 * rectángulo de 28 × 34 mm en el gafete. Lo que NO puede haber dos veces es la
 * decisión de cómo se pide y cuánto vive el objeto — con dos copias, arreglar una
 * fuga de memoria en una deja la otra fugando.
 *
 * ⚠ **Se llama `useFotoPersona` y no `usarFotoPersona`, y es la única excepción al
 * nombrado en español de todo el frontend.** El prefijo `use` no es inglés: es el
 * protocolo por el que React, la regla `useHookAtTopLevel` de Biome y el compilador
 * reconocen un hook. Con `usar`, ninguna de las tres cosas sabe que esto es un hook —
 * lo comprueba el propio linter, que lo rechazó— y nada impediría llamarlo dentro de
 * un `if`, que es exactamente el fallo que esa regla existe para cazar. Los `usarX`
 * que hay son almacenes de Zustand, que no ejecutan hooks en su cuerpo.
 *
 * ⚠ **`tiene_foto` va en la clave de la consulta, no sólo en `enabled`.** Es lo que
 * hace que subir una foto nueva repinte: sin él, la clave no cambia y la caché sirve
 * el objeto viejo hasta que alguien recarga.
 */
export function useFotoPersona(persona: { id: number; tiene_foto: boolean }) {
  return useQuery({
    queryKey: ["persona-foto", persona.id, persona.tiene_foto],
    queryFn: async () =>
      URL.createObjectURL(await pedirBinario(`/api/v1/personas/${persona.id}/foto`)),
    enabled: persona.tiene_foto,
    // El objeto se revoca al cambiar de foto; sin esto, cada reintento dejaría un
    // blob colgando en memoria mientras la pestaña siga abierta.
    gcTime: 0,
  })
}
